import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  Box,
  Collapse,
  Divider,
  InputGroup,
  InputRightElement,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { BankTransferRecipient } from '__generated__/GQL';
import useCurrentLanguage from 'common/hooks/useCurrentLanguage';
import useDebouncedCallback from 'common/hooks/useDebouncedCallback';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import IconButton from 'components/_core/IconButton';
import BackButton from 'components/BackButton';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import { useDevice } from 'helpers/device';
import * as ContentLayout from 'layouts/ContentLayout';
import ErrorLayout from 'layouts/ErrorLayout';
import * as Layout from 'layouts/StepperLayout';

import BankAccountsDetails from '../../../../components/BankAccountsDetails';
import useBankTransferData from '../../../../hooks/useBankTransferData';
import { newTransferNeedsReceipt } from '../../../../utils';
import useInstantScte from '../../hooks/useInstantScte';
import locales from '../../locales';
import ScheduledTransferCardAction from '../../ScheduledTransferCardAction';
import ScheduledTransferSettingsSidebar from '../../ScheduledTransferSettingsSidebar';
import TransferTypeCard from '../../TransferTypeCard';
import { formatAmount, parseAmount } from '../../utils';
import {
  MAX_LABEL_LENGTH,
  TransferDetailsFormSchema,
  transferDetailsFormSchema,
} from './externalTransferDetailsForm.schema';

const NOT_NUMBER_NOR_COMMA_NOR_DOT_REGEX = /[^\d.,]/g;

type TransferDetailsProps = Pick<
  StepProps<BankTransferCreationStep>,
  'goNext'
> &
  Partial<Pick<StepProps<BankTransferCreationStep>, 'goBack'>>;

export const ExternalTransferDetails: FC<TransferDetailsProps> = ({
  goBack,
  goNext,
}) => {
  const device = useDevice();
  const currentLanguage = useCurrentLanguage();
  const { canSendMoneyInstantly, canSendMoneyInstantlyIsLoading } =
    useInstantScte();
  const {
    isOpen: isScheduleSettingsSidebarOpen,
    onClose: onCloseScheduleSettingsSidebar,
    onOpen: onOpenScheduleSettingsSidebar,
  } = useDisclosure();
  const {
    bankTransfer: {
      amount,
      bankTransferRecipientId,
      fromBankAccountId,
      isInstant,
      label,
      schedule,
    },
    setBankTransfer,
  } = useBankTransferContext('setExternalDetails');
  const isCreatingNewScheduledTransfer = Boolean(schedule);
  const { data, error, loading, refetch } = useBankTransferData({
    bankTransferRecipientId,
    fromBankAccountId,
  });
  const [canSendTransferInstantly, setCanSendTransferInstantly] =
    useState(isInstant);
  const [showTransferTypeCard, setShowTransferTypeCard] = useState(
    isInstant !== undefined,
  );

  const balance = data?.fromBankAccount.nextBalance
    ? data.fromBankAccount.nextBalance / 100
    : 0;

  const form = useForm<TransferDetailsFormSchema>({
    defaultValues: {
      amount: amount ? formatAmount(amount, currentLanguage) : '',
      balance,
      frequency: schedule?.frequency,
      isCreatingNewScheduledTransfer,
      label: label ?? '',
    },
    resolver: zodResolver(transferDetailsFormSchema),
  });

  useEffect(() => {
    form.setValue(
      'isCreatingNewScheduledTransfer',
      isCreatingNewScheduledTransfer,
    );
    form.setValue('balance', balance);
    if (isCreatingNewScheduledTransfer) {
      form.setValue('frequency', schedule?.frequency);
    }
    form.getFieldState('amount').error && form.trigger('amount');
  }, [form, isCreatingNewScheduledTransfer, schedule?.frequency, balance]);

  const isAmountAboveThreshold = newTransferNeedsReceipt(
    parseAmount(form.watch('amount'), currentLanguage),
  );

  const lastCheckedAmountRef = useRef<number>();

  const debouncedCheckIfMoneyCanBeSentInstantly = useDebouncedCallback(
    async (
      bankTransferRecipient: Pick<BankTransferRecipient, 'swiftBic' | 'iban'>,
    ) => {
      const parsedAmount = parseAmount(
        form.getValues('amount'),
        currentLanguage,
      );
      if (parsedAmount === undefined || parsedAmount <= 0) {
        return;
      }

      setShowTransferTypeCard(true);
      lastCheckedAmountRef.current = parsedAmount;
      const { data: canSendMoneyInstantlyData } = await canSendMoneyInstantly({
        fetchPolicy: 'no-cache',
        variables: {
          amount: parsedAmount,
          bankAccountId: fromBankAccountId,
          deviceToken: device.token,
          recipientBic: bankTransferRecipient.swiftBic ?? '',
          recipientIban: bankTransferRecipient.iban ?? '',
        },
      });

      if (lastCheckedAmountRef.current === parsedAmount) {
        setCanSendTransferInstantly(
          canSendMoneyInstantlyData?.canSendMoneyInstantly === true,
        );
      }
    },
    500,
  );

  const formAmount = useWatch({
    control: form.control,
    name: 'amount',
  });

  const lastCheckedFormAmountRef = useRef(formAmount);

  if (error) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetch} />
      </ErrorLayout>
    );
  }

  if ((loading && !data) || !data) {
    return <Loader />;
  }

  const {
    fromBankAccount,
    viewer: {
      company: { bankTransferRecipient },
    },
  } = data;

  if (formAmount !== lastCheckedFormAmountRef.current) {
    const parsedAmount = parseAmount(formAmount, currentLanguage);

    const isSameAmount =
      parsedAmount ===
      parseAmount(lastCheckedFormAmountRef.current, currentLanguage);

    lastCheckedFormAmountRef.current = formAmount;

    if (!isSameAmount) {
      setCanSendTransferInstantly(undefined);

      if (parsedAmount === undefined || parsedAmount <= 0) {
        setShowTransferTypeCard(false);
      } else {
        debouncedCheckIfMoneyCanBeSentInstantly(bankTransferRecipient);
      }
    }
  }

  const isTransferTypeCardLoading =
    canSendMoneyInstantlyIsLoading || canSendTransferInstantly === undefined;
  const isContinueButtonDisabled =
    isTransferTypeCardLoading || isScheduleSettingsSidebarOpen;

  return (
    <>
      <Box
        as="form"
        height="100%"
        onSubmit={form.handleSubmit((values) => {
          setBankTransfer((prevState) => ({
            ...prevState,
            amount: parseAmount(values.amount, currentLanguage),
            isInstant:
              !isCreatingNewScheduledTransfer && canSendTransferInstantly,
            label: values.label,
          }));
          goNext();
        })}
      >
        <Layout.Root>
          <Layout.Body>
            <TrackView pageName="NewExternalTransferDetails" />

            <Layout.HeaderButtonsContainer>
              <BackButton onClick={goBack} />
            </Layout.HeaderButtonsContainer>

            <Layout.BodyScrollableSection>
              <ContentLayout.Header title={locales.sendMoney} />

              <VStack alignItems="stretch" spacing="space-40" width="100%">
                <BankAccountsDetails
                  bankTransferRecipient={bankTransferRecipient}
                  emittingBankAccount={fromBankAccount}
                />

                <VStack alignItems="stretch" spacing="space-24">
                  <Form.Field
                    error={form.formState.errors.amount?.message}
                    label={locales.amount}
                  >
                    <Controller
                      control={form.control}
                      name="amount"
                      render={({ field }) => (
                        <InputGroup>
                          <Form.Input
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            data-testid="external-transfer-amount-input"
                            onBlur={() => {
                              if (
                                field.value === '' ||
                                field.value === undefined
                              ) {
                                field.onChange('');
                                return;
                              }
                              field.onChange(
                                formatAmount(
                                  parseAmount(field.value, currentLanguage) ||
                                    0,
                                  currentLanguage,
                                ),
                              );
                              debouncedCheckIfMoneyCanBeSentInstantly.flush();
                            }}
                            onChange={(e) => {
                              const sanitizedValue = e.target.value.replace(
                                NOT_NUMBER_NOR_COMMA_NOR_DOT_REGEX,
                                '',
                              );
                              field.onChange(sanitizedValue);
                            }}
                            type="text"
                            value={field.value}
                          />
                          <InputRightElement paddingRight="space-6">
                            <IconButton
                              aria-label=""
                              borderRadius="radius-8"
                              boxSize={28}
                              icon="euro"
                              iconSize="icon-16"
                              minWidth={28}
                              size="sm"
                              tabIndex={-1}
                              variant="secondary"
                            />
                          </InputRightElement>
                        </InputGroup>
                      )}
                    />
                  </Form.Field>

                  <Form.Field
                    error={form.formState.errors.label?.message}
                    label={locales.label}
                  >
                    <Form.Input
                      {...form.register('label')}
                      maxLength={MAX_LABEL_LENGTH}
                    />
                  </Form.Field>

                  <ScheduledTransferCardAction
                    isSideBarOpen={isScheduleSettingsSidebarOpen}
                    onClick={onOpenScheduleSettingsSidebar}
                  />

                  <Collapse in={showTransferTypeCard}>
                    {isCreatingNewScheduledTransfer ? (
                      <TransferTypeCard type="external_scheduled" />
                    ) : (
                      <TransferTypeCard
                        isLoading={isTransferTypeCardLoading}
                        type={
                          canSendTransferInstantly
                            ? 'external_instant'
                            : 'external_standard'
                        }
                      />
                    )}
                  </Collapse>
                </VStack>
              </VStack>
            </Layout.BodyScrollableSection>

            <Layout.FooterContainer>
              <Divider />
              <Layout.FooterButtonsContainer>
                <Button
                  icon="arrow-right"
                  iconPosition="right"
                  isDisabled={isContinueButtonDisabled}
                  type="submit"
                >
                  {locales.continue}
                </Button>
              </Layout.FooterButtonsContainer>
            </Layout.FooterContainer>
          </Layout.Body>

          <ScheduledTransferSettingsSidebar
            isAmountAboveThreshold={isAmountAboveThreshold}
            isOpen={isScheduleSettingsSidebarOpen}
            onClose={onCloseScheduleSettingsSidebar}
          />
        </Layout.Root>
      </Box>
    </>
  );
};
